import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from '../components/ui/sonner';
import { isAuthenticated } from '../lib/auth';

const Login = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    // Redirect if already logged in (only check once on mount)
    if (isAuthenticated()) {
      navigate('/dashboard/home', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store user data and token in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('token', data.token);
      localStorage.setItem('isAuthenticated', 'true');

      toast.success('Login successful!', {
        description: `Welcome back, ${data.user.name}!`,
      });

      // Redirect to dashboard
      navigate('/dashboard/home');
    } catch (error) {
      console.error('Error during login:', error);
      toast.error('Login failed', {
        description: error.message || 'Invalid email or password. Please try again.',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1c1b] px-4">
      <Card className="w-full max-w-md border-[#3f4816] bg-[#302f2c]">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-[#d9fb06] text-center">
            Employee Login
          </CardTitle>
          <CardDescription className="text-center text-[#888680]">
            Enter your credentials to access the dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#d9fb06]">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-[#888680]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="employee@example.com"
                  required
                  disabled={isSubmitting}
                  className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#d9fb06]">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-[#888680]" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  required
                  disabled={isSubmitting}
                  className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e005] font-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
